import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export const useSessionMonitor = () => {
  const navigate = useNavigate();
  const cleanupInProgressRef = useRef(false);

  useEffect(() => {
    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'TOKEN_REFRESHED') {
          console.log('Token refreshed successfully');
        }
        
        if (event === 'SIGNED_OUT') {
          console.log('User signed out');
          navigate('/auth');
        }
        
        // Handle expired/invalid tokens
        if (!session && event === 'USER_UPDATED') {
          console.log('Session invalid, redirecting to login');
          toast.error('Tu sesión ha expirado. Por favor, volvé a iniciar sesión.');
          navigate('/auth');
        }
      }
    );

    // Check session validity on mount and clean corrupted sessions automatically
    const checkSession = async () => {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      // Detect and clean corrupted sessions automatically
      if (error || (session && !session.user)) {
        console.error('Sesión corrupta detectada, limpiando automáticamente:', error);
        
        if (!cleanupInProgressRef.current) {
          cleanupInProgressRef.current = true;
          
          // Clean all Supabase data from localStorage
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('sb-')) {
              localStorage.removeItem(key);
            }
          });
          
          // Force local signOut
          await supabase.auth.signOut({ scope: 'local' });
          
          // Show message with recovery action
          toast.error('Sesión inválida detectada.', {
            action: {
              label: 'Limpiar datos',
              onClick: () => {
                localStorage.clear();
                window.location.reload();
              }
            }
          });
          
          navigate('/auth', { replace: true });
          setTimeout(() => { cleanupInProgressRef.current = false; }, 5000);
        }
        return;
      }
      
      // Check if session is expired
      if (session?.expires_at) {
        const expiresAt = new Date(session.expires_at * 1000);
        const now = new Date();
        
        if (expiresAt < now) {
          console.log('Sesión expirada detectada, limpiando...');
          
          if (!cleanupInProgressRef.current) {
            cleanupInProgressRef.current = true;
            
            // Clean localStorage
            Object.keys(localStorage).forEach(key => {
              if (key.startsWith('sb-')) {
                localStorage.removeItem(key);
              }
            });
            
            await supabase.auth.signOut({ scope: 'local' });
            toast.error('Tu sesión ha expirado. Por favor, volvé a iniciar sesión.');
            navigate('/auth', { replace: true });
            setTimeout(() => { cleanupInProgressRef.current = false; }, 5000);
          }
        }
      }
    };

    checkSession();

    // Safety timeout: if after 10 seconds there's still a session error, force cleanup
    const safetyTimeout = setTimeout(async () => {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error && !session) {
        console.error('Safety timeout: forzando limpieza de sesión corrupta');
        
        if (!cleanupInProgressRef.current) {
          cleanupInProgressRef.current = true;
          
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('sb-')) {
              localStorage.removeItem(key);
            }
          });
          
          await supabase.auth.signOut({ scope: 'local' });
          navigate('/auth', { replace: true });
          setTimeout(() => { cleanupInProgressRef.current = false; }, 5000);
        }
      }
    }, 10000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(safetyTimeout);
    };
  }, [navigate]);
};
